import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import DashboardHeader from "../../components/DashboardHeader";
import {useUserContext} from "../../contexts/user_context";
import {Brand, Border, Ink, Motion, Radii, Shadows, Surface} from "../../colors";
import {NavigationState, useNavigationContext} from "../../contexts/NavContext/nav-context";
import {AppView, AppViewToLinkMap} from "../../App";
import {FcGoogle} from "react-icons/fc";
import {BsMicrosoft} from "react-icons/bs";
import {warmBackend, consumeOAuthState} from "../../api";

const SignupOrLogin: React.FC<{ initialMode: "login" | "signup" | "callback" }> = ({
                                                                                       initialMode = "login",
                                                                                   }) => {
    const {navigateTo} = useNavigationContext()!;
    const {loginGoogle, loginMicrosoft, authenticateOAuth, verifyEmailCode} = useUserContext()!;
    const navigate = useNavigate();

    // Email verification state
    const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
    const [pendingAuthToken, setPendingAuthToken] = useState<string | null>(null);
    const [verificationEmail, setVerificationEmail] = useState<string | null>(null);
    const [verificationCode, setVerificationCode] = useState("");
    const [isVerifying, setIsVerifying] = useState(false);
    const [verificationError, setVerificationError] = useState<string | null>(null);
    // True while exchanging the OAuth code — covers the case where /auth/callback
    // lands on a still-cold backend and takes a few seconds.
    const [isAuthenticating, setIsAuthenticating] = useState(
        () => initialMode === "callback" && new URLSearchParams(window.location.search).has("code"),
    );

    useEffect(() => {
        if (initialMode === "callback") {
            const params = new URLSearchParams(window.location.search);
            const code = params.get("code");
            if (!code) {
                setIsAuthenticating(false);
                return;
            }
            // CSRF: the state we sent must come back unchanged.
            if (!consumeOAuthState(params.get("state"))) {
                console.error("OAuth state mismatch — aborting sign-in");
                setIsAuthenticating(false);
                navigate(AppViewToLinkMap[AppView.Login]);
                return;
            }
            authenticateOAuth(code).then(
                (authResponse) => {
                    if (authResponse.email_verification_required) {
                        // Show email verification UI
                        setEmailVerificationRequired(true);
                        setPendingAuthToken(authResponse.pending_authentication_token || null);
                        setVerificationEmail(authResponse.email || null);
                        setIsAuthenticating(false);
                    } else if (authResponse.user) {
                        navigateTo({view: AppView.MyDashboard} as NavigationState);
                    } else {
                        setIsAuthenticating(false);
                    }
                }
            ).catch((err) => {
                console.error("OAuth sign-in failed", err);
                setIsAuthenticating(false);
                navigate(AppViewToLinkMap[AppView.Login]);
            });
        } else {
            // Login form is showing — warm the (possibly scaled-to-zero) backend now,
            // so it's ready by the time the OAuth callback exchanges the code.
            warmBackend();
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("provider") && params.get("token")) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, []);

    const handleVerifyEmail = async () => {
        if (!pendingAuthToken || !verificationCode.trim()) return;

        setIsVerifying(true);
        setVerificationError(null);

        try {
            const user = await verifyEmailCode(verificationCode.trim(), pendingAuthToken);
            if (user) {
                // Use direct navigate to avoid race condition with context's currentUser check
                navigate(AppViewToLinkMap[AppView.MyDashboard]);
            }
        } catch (error: unknown) {
            // Extract the actual error message from axios response
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const axiosError = error as any;
            const detail = axiosError?.response?.data?.detail;
            const errorMessage = detail || "Verification failed. Please try again.";
            setVerificationError(errorMessage);
        } finally {
            setIsVerifying(false);
        }
    };

    // Check if verification code looks valid (WorkOS uses 6-digit codes)
    const isCodeValidLength = verificationCode.trim().length === 6;

    const pageShell = (inner: React.ReactNode) => (
        <section className="min-h-screen">
            <div className="flex min-h-screen">
                <div className="flex-none">
                    <DashboardHeader />
                </div>
                <div className={`flex flex-1 items-center justify-center ${Surface.tinted} text-left px-6`}>
                    <div className={`w-full max-w-md ${Surface.base} ${Border.DEFAULT} ${Radii.xl} ${Shadows.soft} p-8`}>
                        {inner}
                    </div>
                </div>
            </div>
        </section>
    );

    if (isAuthenticating) {
        return pageShell(
            <div className="flex flex-col items-center text-center py-6">
                <div className="h-10 w-10 rounded-full border-2 border-[#ede9f7] border-t-[#6a4eaa] animate-spin" />
                <h2 className={`mt-5 text-xl font-semibold tracking-tight ${Ink.primary}`}>Signing you in…</h2>
                <p className={`mt-2 text-sm ${Ink.secondary}`}>Just a moment while we get everything ready.</p>
            </div>
        );
    }

    if (emailVerificationRequired) {
        return pageShell(
            <>
                <h2 className={`text-2xl font-semibold tracking-tight ${Ink.primary}`}>Verify your email</h2>
                <p className={`mt-2 text-sm ${Ink.secondary}`}>
                    We&apos;ve sent a verification code to{" "}
                    <span className={`font-medium ${Ink.primary}`}>{verificationEmail || "your email"}</span>.
                </p>

                {verificationError && (
                    <div className="mt-5 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                        {verificationError}
                    </div>
                )}

                <div className="mt-6">
                    <label htmlFor="verification-code" className={`block text-xs font-medium uppercase tracking-wide ${Ink.muted} mb-2`}>
                        Verification code
                    </label>
                    <input
                        id="verification-code"
                        type="text"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        placeholder="123456"
                        className={`w-full px-4 py-3 ${Border.DEFAULT} ${Radii.md} ${Surface.base} ${Ink.primary} placeholder:text-zinc-400 ${Brand.ring} ${Motion.fast}`}
                        disabled={isVerifying}
                        autoFocus
                    />
                </div>

                <button
                    onClick={handleVerifyEmail}
                    disabled={isVerifying || !verificationCode.trim()}
                    className={`mt-6 w-full py-3 px-4 ${Radii.md} font-semibold ${Motion.base} ${Brand.ring} ${
                        isVerifying || !verificationCode.trim()
                            ? "bg-zinc-200 text-zinc-500 cursor-not-allowed"
                            : isCodeValidLength
                                ? `${Brand.solid} ${Brand.solidHover} ${Shadows.glow}`
                                : `${Brand.soft} ${Brand.softHover}`
                    }`}
                >
                    {isVerifying ? "Verifying…" : "Verify email"}
                </button>

                <div className="mt-6 pt-5 border-t border-[#f4f4f6]">
                    <p className={`text-xs text-center ${Ink.muted} mb-3`}>
                        Didn&apos;t receive the code? Check your spam folder.
                    </p>
                    <button
                        onClick={() => {
                            setEmailVerificationRequired(false);
                            setVerificationCode("");
                            setVerificationError(null);
                        }}
                        className={`w-full py-2 px-4 ${Radii.md} text-sm font-medium ${Ink.secondary} ${Surface.sunken} hover:bg-[#e8e8ec] ${Motion.fast}`}
                    >
                        Resend code
                    </button>
                    <p className={`mt-2 text-[11px] text-center ${Ink.subtle}`}>
                        This will restart the sign-in process and send a new code
                    </p>
                </div>
            </>
        );
    }

    return pageShell(
        <>
            <h2 className={`text-2xl font-semibold tracking-tight ${Ink.primary}`}>Sign in</h2>
            <p className={`mt-2 text-sm ${Ink.secondary}`}>Continue with your work account.</p>

            <div className="mt-6 flex flex-col gap-3">
                <button
                    onClick={loginGoogle}
                    className={`flex w-full items-center justify-center gap-3 ${Radii.md} ${Border.DEFAULT} ${Surface.base} px-4 py-3 text-sm font-medium ${Ink.secondary} ${Shadows.sm} hover:bg-[#fafafb] hover:border-[#d1d1d8] hover:shadow-md ${Brand.ring} ${Motion.base}`}
                >
                    <FcGoogle className="h-5 w-5" />
                    Continue with Google
                </button>
                <button
                    onClick={loginMicrosoft}
                    className={`flex w-full items-center justify-center gap-3 ${Radii.md} ${Border.DEFAULT} ${Surface.base} px-4 py-3 text-sm font-medium ${Ink.secondary} ${Shadows.sm} hover:bg-[#fafafb] hover:border-[#d1d1d8] hover:shadow-md ${Brand.ring} ${Motion.base}`}
                >
                    <BsMicrosoft className="h-4 w-4 text-[#00a4ef]" />
                    Continue with Microsoft
                </button>
            </div>
        </>
    );
};

export default SignupOrLogin;
